import { mutations } from 'entities';

import {
  Box,
  Button,
  ButtonProps,
  Grid,
  LabeledInput,
  LabeledSelect,
  ResponsiveOverlay,
  Row,
} from 'components/Blocks';
import { InfoIcon, WarningIcon } from 'components/Blocks/Icons';
import { Coin } from 'components/DecorativeIcons';

import { RedeemSuccess } from './RedeemSuccess';

import { appConfig } from 'config/app';
import { useLang } from 'locale';
import { useDashboard } from 'providers/Auth';
import { useFormControlV2, useToggleV2 } from 'hooks';

import { arrayFrom, getFirst, listGet } from 'utils/array';
import { emptyFunction } from 'utils/function';

import { getAvailableRewards, transformRewardPayload } from './config';
import { EmailConfirm } from 'components/EmailConfirmation';

interface RedeemButtonProps {
  className?: string;
  size?: ButtonProps['size'];
}

const MIN = appConfig.minimumRedemption;

type RedeemForm = { type: string; amount: string } & Record<string, string>;

function getPointsOptions(currentIncentive: number) {
  const count = Math.floor(currentIncentive / MIN);

  return arrayFrom(count, (index) => index * MIN + MIN).map((value) => ({
    label: value,
    value: `${value}`,
  }));
}

export function RedeemButton(props: RedeemButtonProps) {
  const { t } = useLang();

  const [{ user }, { updateUser }] = useDashboard();

  const [isOpen, openHandle] = useToggleV2();
  const [isSuccess, successHandle] = useToggleV2();

  const canRedeem = user.activeIncentive >= MIN;

  const redeemPointsOptions = getPointsOptions(user.activeIncentive);

  const hasMultiRedeem = redeemPointsOptions.length > 1;

  const [form, { handleChange, dispatch, resetDefault }] = useFormControlV2<RedeemForm>({
    type: '',
    amount: hasMultiRedeem ? '' : getFirst(redeemPointsOptions)?.value || '',
  });

  const availableRewards = getAvailableRewards(user.country);

  const selectedType = listGet(availableRewards, form.type);

  const onTypeChange = (type: string) => {
    const newType = listGet(availableRewards, type)!;

    const newValues = newType.formEntries.map((config) => [
      config.name,
      config.getDefaultValue(user),
    ]);

    dispatch({
      ...Object.fromEntries(newValues),
      type,
    });
  };

  const disabled =
    !form.type ||
    !form.amount ||
    selectedType?.formEntries.some((config) => config.validate?.(form[config.name], form));

  const redeem = mutations.rewards.requestRedemption.use({
    block: disabled,

    params: {
      type: form.type,
      amount: Number(form.amount),
      ...transformRewardPayload(form.type, form),
    },

    onSuccess: () => {
      updateUser({ activeIncentive: user.activeIncentive - Number(form.amount) });
      successHandle.turnOn();
    },
  });

  const closeAll = () => {
    openHandle.turnOff();

    resetDefault();
    successHandle.turnOff();
  };

  if (!canRedeem && !isSuccess)
    return (
      <Button {...props} disabled={!canRedeem}>
        {t('redeem')}
      </Button>
    );

  return (
    <>
      {user.emailConfirmed ? (
        <Button {...props} onClick={openHandle.turnOn}>
          {t('redeem')}
        </Button>
      ) : (
        <EmailConfirm reason="REDEMPTION" trigger={<Button>{t('redeem')}</Button>} />
      )}

      <ResponsiveOverlay
        isOpen={isOpen}
        close={openHandle.turnOff}
        showCloseButton={false}
        allowEscape={false}
        width="min(80vh, 600px)"
        className={isSuccess ? '' : 'overflow-y-auto'}
        gap="6"
      >
        {isSuccess ? (
          <RedeemSuccess close={closeAll} />
        ) : (
          <>
            <Row justifyContent="center">
              <Coin size={42} />
              <h2 className="text-lg font-medium">
                {t('redeem-balance')} <strong>{user.activeIncentive}</strong> {t('points')}
              </h2>
            </Row>

            <p className="text-center text-sm">{t('redeem-description')}</p>

            <Grid cols={2} className="phone:grid-cols-1">
              <LabeledSelect
                isSearchable={false}
                label={t('payment-preference')}
                size="large"
                value={form.type}
                name="type"
                onValueChange={onTypeChange}
                options={availableRewards.map(({ selectLabel, id }) => ({
                  value: id,
                  label: t(selectLabel),
                }))}
              />

              <LabeledSelect
                isSearchable={false}
                size="large"
                label={t('redeem-points')}
                options={redeemPointsOptions}
                value={form.amount}
                disabled={!hasMultiRedeem}
                onChange={handleChange}
                name="amount"
              />
            </Grid>

            {!!selectedType && (
              <>
                <Row className="rounded-md border border-warning bg-warning/5 p-3">
                  <Box border="circle" className="bg-warning/20 p-1.5 text-warning">
                    <WarningIcon size={24} />
                  </Box>

                  <p className="text-sm">{t(selectedType.alert)}</p>
                </Row>

                <Grid
                  cols={selectedType.formEntries.length > 1 ? 2 : 1}
                  className="phone:grid-cols-1"
                >
                  {selectedType.formEntries.map((inputConfig) => {
                    const value = form[inputConfig.name] ?? inputConfig.getDefaultValue(user);
                    const error = inputConfig.validate?.(value, form);

                    return (
                      <LabeledInput
                        key={inputConfig.name}
                        name={inputConfig.name}
                        label={t(inputConfig.label)}
                        value={value}
                        disabled={inputConfig.disabled}
                        onChange={inputConfig.disabled ? emptyFunction : handleChange}
                        error={error ? t(error) : undefined}
                      />
                    );
                  })}
                </Grid>
              </>
            )}

            <Row className="rounded-md border border-primary p-3">
              <Box border="circle" className="bg-primary/20 p-1.5 text-primary">
                <InfoIcon size={24} />
              </Box>

              <p className="text-sm">{t('redeem-time-warning')}</p>
            </Row>

            <Grid cols={2} className="phone:grid-cols-1">
              <Button
                size="large"
                variant="outlined"
                color="gray"
                onClick={openHandle.turnOff}
                className="phone:order-2"
              >
                {t('cancel')}
              </Button>

              <Button
                size="large"
                className="phone:order-1"
                disabled={disabled}
                {...redeem.buttonProps}
              >
                {t('redeem')}
              </Button>
            </Grid>
          </>
        )}
      </ResponsiveOverlay>
    </>
  );
}
